import { ComponentProps, useEffect, useRef } from "react";
import { styled } from "styled-components";

import { Text } from "@vericus/cadmus-ui";

const FocusableTitle = styled(Text)`
  /* Focused programmatically on mount and never reachable by tabbing, so the
     browser's default ring would only read as a rendering artefact. */
  &:focus {
    outline: none;
  }
`;

/**
 * `Text` declares `as` as required, but styled-components resolves it from the
 * `kind` mapping, so callers never pass it.
 */
type Props = Omit<ComponentProps<typeof Text>, "as"> & { as?: string };

/**
 * Title for a view, which takes focus when it mounts.
 *
 * Use this wherever an action replaces what the user is looking at, rather
 * than a plain `Text`. In a single page app the browser does nothing on a
 * view change, so focus stays where it was, usually on <body>. Assistive
 * technology then announces nothing and the user has to go looking for what
 * changed. Moving focus to the title announces the new view and puts the user
 * at the top of it.
 */
export function FocusTitle(props: Props) {
  const ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  return <FocusableTitle {...props} ref={ref} tabIndex={-1} />;
}
